import { z } from 'zod';
import { request, useQuery } from '@applyboard/ui-utils';

export const strapiProxyUrl = '/api/v1/cms';
const WhatsNewItemAttributesSchema = z.object({
  Title: z.string().nullable().optional(),
  Description: z.string().nullable().optional(),
  CTA_Text: z.string().nullable().optional(),
  Tag_Text: z.string().nullable().optional(),
  Tag_Intent: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  publishedAt: z.string().nullable().optional(),
});

const WhatsNewItemSchema = z.object({
  id: z.number(),
  attributes: WhatsNewItemAttributesSchema,
});

const WhatsNewPaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  total: z.number(),
});

const WhatsNewResponseSchema = z.object({
  data: z.array(WhatsNewItemSchema),
  meta: z.object({
    pagination: WhatsNewPaginationSchema,
  }),
});

export type WhatsNewItemAttributes = z.infer<typeof WhatsNewItemAttributesSchema>;
export type WhatsNewItem = z.infer<typeof WhatsNewItemSchema>;
export type WhatsNewResponse = z.infer<typeof WhatsNewResponseSchema>;

const WHATS_NEW_URL = `${strapiProxyUrl}/api/global-solutions-whats-news?populate=deep&sort=publishedAt:desc`;

const fetchWhatsNew = async (): Promise<WhatsNewResponse> => {
  const response = await request(WHATS_NEW_URL, {
    method: 'GET',
    schema: WhatsNewResponseSchema,
    throwError: async (response: Response) => {
      const jsonMessage = await response.json();
      throw new Error(jsonMessage.message || 'Error fetching Whats New content');
    },
  });

  return response;
};

interface UseWhatsNewOptions {
  enabled?: boolean;
  retryCount?: number;
  retryOnMount?: boolean;
}

export const useWhatsNew = (options: UseWhatsNewOptions = {}) => {
  const { enabled = true, retryCount = 3, retryOnMount = false } = options;

  const { data, isLoading, isError, error } = useQuery(['whats-new'], fetchWhatsNew, {
    enabled,
    retry: retryCount,
    retryOnMount,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Map the response to a simplified format for the component
  const mappedItems =
    data?.data.map(item => ({
      id: item.id,
      Title: item.attributes.Title,
      Description: item.attributes.Description,
      CTA_Text: item.attributes.CTA_Text,
      Tag_Text: item.attributes.Tag_Text,
      Tag_Intent: item.attributes.Tag_Intent,
      createdAt: item.attributes.createdAt,
      updatedAt: item.attributes.updatedAt,
      publishedAt: item.attributes.publishedAt,
    })) || [];

  return {
    whatsNewItems: mappedItems,
    isLoadingWhatsNew: isLoading,
    isWhatsNewError: isError,
    whatsNewError: error,
    pagination: data?.meta.pagination,
  };
};
